import classNames from 'classnames';
import React, { ChangeEvent, ReactElement, useState } from 'react';

import { IUseState } from '../../../../shared/types/IUseState';

import styles from './TextInput.styles.css';

export interface ITextInputProps {
    label: string;
    value?: string;
    placeholder?: string;
    type?: string;
    name?: string;
    errorMessage?: string;
    successMessage?: string;
    disabled?: boolean;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
    'data-testid'?: string;
    className?: string;
}

export function TextInput (props: ITextInputProps): ReactElement {
    const {
        label,
        value = '',
        type = 'text',
        name = '',
        errorMessage,
        successMessage,
        disabled = false,
        onChange,
        onBlur,
        'data-testid': dataTestId = '',
        className = '',
    }: ITextInputProps = props;

    let { placeholder = '' }: ITextInputProps = props;
    const [isFocused, setIsFocused]: IUseState<boolean> = useState<boolean>(false);

    const wrapperClassName: string = classNames({
        'w-full h-8 flex justify-center relative rounded-sm bg-white': true,
        'border': (!errorMessage && !successMessage),
        'text-rebrand-red-500 border-2': (errorMessage && !disabled),
        'text-rebrand-green-500 border-2': (successMessage && !disabled),
        'text-black-30': disabled,
        [className]: !!className,
        [styles.wrapper]: true,
    });

    const inputClassName: string = classNames({
        'text-med font-gellix w-full pl-2 outline-none bg-transparent': true,
        'text-gray-600': (!isFocused && !value),
        'text-gray-900': !!value,
        'mt-3': (isFocused || !!value),
        [styles.input]: (isFocused || !!value),
    });

    const labelClassName: string = classNames({
        'text-xs text-gray-700 top-1/2 font-gellix font-medium w-full absolute left-0 pl-2': true,
        'transition-opacity ease-in-out duration-200': true,
        'font-medium': (!!errorMessage && !disabled),
        'flex opacity-00': (!isFocused && !value),
    });

    function handleBlur (event: ChangeEvent<HTMLInputElement>): void {
        setIsFocused(false);

        if (onBlur) {
            onBlur(event);
        }
    }

    function handleFocus (): void {
        setIsFocused(true);
    }

    if (isFocused) {
        placeholder = '';
    }

    return (
        <div className="font-gellix" data-testid={dataTestId}>
            <div className={wrapperClassName}>
                <label htmlFor="oz-input" className={labelClassName} role="label">
                    {label}
                </label>
                <input
                    id="oz-input"
                    name={name}
                    type={type}
                    className={inputClassName}
                    disabled={disabled}
                    placeholder={placeholder}
                    onChange={onChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    value={value}
                    role="input"
                />
            </div>
            {
                errorMessage &&
                <p className="text-rebrand-red-500 text-base mt-1/2 font-regular font-gellix">
                    {errorMessage}
                </p>
            }
            {
                successMessage &&
                <p className="text-rebrand-green-500 text-base mt-1/2 font-regular font-gellix">
                    {successMessage}
                </p>
            }
        </div>
    );
}
